/**
 * Ordering rules for the attendance tables.
 *
 * Kept apart from tableUi.tsx — which is all JSX chrome — so the comparison semantics are
 * plain functions that can be reasoned about and tested on their own. The React binding
 * (`useSort`) and the header cell (`SortableTH`) live in tableUi and call into this.
 */

export type SortDir = 'asc' | 'desc';

export interface SortState {
  /** null = the list's natural order, i.e. whatever the server returned. */
  key: string | null;
  dir: SortDir;
}

/**
 * Values that carry nothing to order by. NaN counts: `timeKey` yields it for unparseable
 * timestamps, and NaN compares false against everything, which would otherwise scatter
 * those rows unpredictably through the result.
 */
export function isBlank(v: unknown): boolean {
  return v === null || v === undefined || v === '' || (typeof v === 'number' && Number.isNaN(v));
}

/**
 * Compare two non-blank cell values. Numbers numerically; everything else as text with
 * `numeric` collation, so "Employee 2" precedes "Employee 10" instead of following it.
 */
export function compareValues(a: unknown, b: unknown): number {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

/**
 * Order a copy of `items` by the value `get` extracts from each row.
 *
 * Blank values sort last in BOTH directions. Someone who never clocked in has no "first
 * in"; parking those rows at the top of an ascending sort would bury the ones actually
 * being looked for. Ties keep their input order (Array.prototype.sort is stable), so a
 * secondary ordering established upstream survives.
 */
export function sortRows<T>(items: T[], get: (row: T) => unknown, dir: SortDir): T[] {
  const mul = dir === 'asc' ? 1 : -1;
  // Copy first: `items` is usually derived from state, and sort() mutates in place.
  return [...items].sort((x, y) => {
    const a = get(x);
    const b = get(y);
    const aBlank = isBlank(a);
    const bBlank = isBlank(b);
    // Direction deliberately not applied — blanks stay at the bottom either way.
    if (aBlank || bBlank) return aBlank && bBlank ? 0 : aBlank ? 1 : -1;
    return compareValues(a, b) * mul;
  });
}

/**
 * Timestamp accessor for sorting — epoch ms, or null when absent/unparseable.
 *
 * Sort on this rather than on the rendered string: "9:05 AM" sorts after "10:15 AM"
 * lexically, which is exactly the bug this exists to prevent.
 */
export function timeKey(ts?: string | number | null): number | null {
  if (ts === null || ts === undefined || ts === '') return null;
  const ms = typeof ts === 'number' ? ts : new Date(ts).getTime();
  return Number.isNaN(ms) ? null : ms;
}
